import {
  ApplicationAppointments,
  ApplicationSummaryEnhanced,
} from "models/ApplicationSummaryEnhanced"
import { Appointment, Flag, FullApplication } from "models/FullApplication"
import { EventAction, EventCategory } from "models/GoogleAnalytics"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { HandoffFields, writeHandoff } from "lib/JourneyHandoff"

/**
 * The split fact find ("mortgage profile") is not part of this app: it is the discovery app in the
 * monorepo, served from the same origin. This is the monorepo's `SPLIT_FF_PATH`
 * (packages/blueprint/BookedAppointment/BookedAppointment.tsx), so change it there first.
 */
export const SPLIT_FF_PATH = "/discovery/mortgage-profile"

/**
 * Where someone came into the split fact find from, recorded with the "Split FF Entry Source" event.
 * Add a value when a new route in is added (the CRM, say). `Onboarding` is the route from the
 * booking confirmation in the discovery app, which should log the same event with this value so
 * the routes can be compared.
 */
export enum SplitFfEntrySource {
  myMojo = "MyMojo",
  onboarding = "Onboarding",
}

/**
 * The variant arms of the monorepo's `split-ff-next-steps` experiment, which is what this page is
 * for. Bucketing happens server side in funnel-api (`bucketSplitFactFindNextSteps`), is written to
 * the opportunity's `Current_Experiments__c`, and reaches us as `/status`'s `currentExperiments`.
 *
 * Only the variant arms are listed: `split-ff-next-steps-control-v4` is the other arm of the same
 * experiment, and those users are meant to see the old next steps, so matching it here would make
 * the experiment measure nothing.
 */
export const SPLIT_FF_NEXT_STEPS_VARIANTS = [
  "split-ff-next-steps-variant-v4",
  "split-ff-next-steps-variant-v5",
] as const

/**
 * Whether this application is in a variant arm. `currentExperiments` is every experiment the
 * opportunity has been bucketed into, joined with ";" (see the ingestor's
 * `mapAppendExperimentFlags`), so it is split rather than searched: "...-variant-v45" or a flag
 * that merely contains one of these mustn't count.
 *
 * Null or absent means we don't know, which is false: showing the new page to someone who might be
 * in the control arm would spoil the experiment.
 */
export const isInSplitFfNextStepsVariant = (
  currentExperiments: string | null | undefined
): boolean =>
  (currentExperiments ?? "")
    .split(";")
    .some((experiment) =>
      (SPLIT_FF_NEXT_STEPS_VARIANTS as readonly string[]).includes(experiment.trim())
    )

const ELIGIBLE_TLS_COLOUR = "green"
const ELIGIBLE_TLS_TIERS = ["a", "b", "c"]

/** Green A, B or C: the traffic lights that are offered the split fact find. */
export const isGreenAbcTls = (tls: Partial<ApplicationSummaryEnhanced["tls"]> | null | undefined) =>
  tls?.colour?.trim().toLowerCase() === ELIGIBLE_TLS_COLOUR &&
  ELIGIBLE_TLS_TIERS.includes(tls?.tier?.trim().toLowerCase() ?? "")

/** What both Salesforce's list of appointments and the application's own list have */
interface Booking {
  appointmentDate?: string | null
  assignee?: string | null
  isCancelled?: boolean | null
}

type DatedBooking<T extends Booking> = T & { appointmentDate: string }

/** The next appointment that is still to come: not cancelled, and the soonest if there are several. */
export const getUpcomingAppointment = <T extends Booking>(
  appointments: T[] | null | undefined,
  now = new Date()
): DatedBooking<T> | undefined =>
  (appointments ?? [])
    .filter(
      (appointment): appointment is DatedBooking<T> =>
        !!appointment.appointmentDate && !appointment.isCancelled
    )
    // An invalid date is NaN, which is never after now
    .map((appointment) => ({ appointment, time: new Date(appointment.appointmentDate).getTime() }))
    .filter(({ time }) => time > now.getTime())
    .sort((a, b) => a.time - b.time)[0]?.appointment

/**
 * The booking to show the customer. Salesforce's list is what the rest of the page goes by, so it
 * wins. The application's own list is used only when Salesforce has no appointment at all: a
 * booking is written to the application at once but reaches Salesforce through a queue, which can
 * be behind or fail, and the customer would otherwise be asked to book again. Once Salesforce has
 * any appointment, even a cancelled one, it is trusted alone, so a booking cancelled there isn't
 * brought back by the application's older copy.
 */
export const getUpcomingBooking = (
  summaryAppointments: ApplicationAppointments[] | null | undefined,
  applicationAppointments: Appointment[] | null | undefined,
  now = new Date()
) =>
  summaryAppointments?.length
    ? getUpcomingAppointment(summaryAppointments, now)
    : getUpcomingAppointment(applicationAppointments, now)

/**
 * The expert's name to show, if the booking has one. Salesforce has their name, but the
 * application's copy of a booking only has the calendar's email address, which isn't shown.
 */
export const getExpertName = (assignee: string | null | undefined): string | undefined =>
  assignee && !assignee.includes("@") ? assignee : undefined

/** The Salesforce objects the split fact find writes, which `/split-fact-find/{friendlyId}` reads. */
export type SplitFactFindObject =
  | "Opportunity"
  | "Deposit_source__c"
  | "Applicant__c"
  | "Dependants__c"
  | "Expenditure__c"
  | "Income_Employment__c"
  | "Property__c"
  | "PropertyFlag__c"
  | "PropertyFieldConfirmation__c"

/** The records Salesforce holds per object, each field as Salesforce has it (null when empty). */
export type SplitFactFindRecords = Record<SplitFactFindObject, Record<string, unknown>[]>

/**
 * What `GET /split-fact-find/{friendlyId}` returns: the records Salesforce holds for each object
 * the split fact find writes (listed in the monorepo's `split-ff-salesforce-writes.md`), and the
 * objects whose query Salesforce rejected. The caller has to own the application.
 *
 * An object named in `failed` has `[]` in `records`, which means "could not be read" rather than
 * "there are none" (see `GetSplitFactFind` in my.mojo.api). Only a failed Opportunity is fatal
 * there, so the objects progress is worked out from can both come back this way.
 */
export type SplitFactFindData = {
  records: SplitFactFindRecords
  failed: SplitFactFindObject[]
}

/**
 * How far someone has got with the split fact find, worked out from what Salesforce holds. Each
 * section is written when it is submitted, and nothing else writes these records before an
 * appointment, so finding them tells us the section was done.
 *
 * Employment and support needs are left out: employment only applies to some applicants, and
 * support needs is also written by the booking step.
 */
export interface SplitFactFindProgress {
  /** The property section, the first: a property record is linked to the opportunity */
  property: boolean
  /** The personal section, the last: an applicant's answers have been saved */
  personal: boolean
  /** Every section has been submitted, which is the property and personal sections */
  complete: boolean
}

/** The objects progress is read from. An empty list for either only counts if it was read. */
const PROGRESS_OBJECTS: SplitFactFindObject[] = ["Property__c", "Applicant__c"]

/**
 * Null when there is nothing to go on: the split fact find couldn't be read at all, or Salesforce
 * rejected the query for one of the objects progress is read from. Their records are then `[]`,
 * which would otherwise read as "nothing completed" and show a finished profile as outstanding.
 */
export const getSplitFactFindProgress = (
  data: SplitFactFindData | null | undefined
): SplitFactFindProgress | null => {
  if (!data?.records) return null
  if (PROGRESS_OBJECTS.some((object) => data.failed?.includes(object))) return null

  const property = (data.records.Property__c?.length ?? 0) > 0
  // The main fact find never writes marital status, so an applicant having one means they got to
  // the end of the personal section
  const personal = (data.records.Applicant__c ?? []).some(
    (applicant) => !!applicant.Marital_Status1_Applicant__c
  )

  return { property, personal, complete: property && personal }
}

/**
 * Whether the split fact find is done, or `undefined` until the application has loaded (until then
 * it is an empty object, not null).
 *
 * What Salesforce holds of the customer's answers decides (`progress`): the split fact find saves
 * them there and nowhere else. The `CompletedFactFindProperty` flag is not the answer. The main
 * funnel sets it when it creates a remortgage or a post-offer purchase, which are the journeys
 * that get Green traffic lights, so it says nothing about whether the split fact find was done. It
 * is only the fallback for when Salesforce couldn't be read (`progress` is null or absent).
 *
 * Note that the split fact find itself takes that flag to mean "already submitted" and goes
 * straight to its completed page, so for an application that has it, the questions can't be
 * reached from here until that changes.
 */
export const hasCompletedSplitFactFind = (
  progress: SplitFactFindProgress | null | undefined,
  application: FullApplication | null | undefined
): boolean | undefined => {
  if (!application?.applicationId) return undefined
  if (progress) return progress.complete
  return application.metadata?.flags?.includes(Flag.CompletedFactFindProperty) ?? false
}

/** The property's postcode, which the split fact find validates against the application's address. */
export const getSplitFfPostcode = (
  application: FullApplication | null | undefined,
  summary: ApplicationSummaryEnhanced | null | undefined
): string | undefined =>
  application?.completePropertyDetails?.propertyAddress?.postcode ||
  summary?.propertyAddress?.postcode ||
  undefined

// The split fact find reads which application to open, and the postcode to validate it with, from
// a session it keeps in sessionStorage, rather than from the url (see `startSession` in the
// monorepo's apps/discovery/src/pages/split-factfind-pages/shared/session.ts). So that session has
// to be written before sending the user in. The key and version must match theirs: if they bump
// the version, an entry written here is ignored and the user lands on the split fact find's error
// page. Their `VERSION` was 3 when this was first written and had moved to 5 by 2026-10-30, so
// check it against theirs whenever the split fact find changes.
const SPLIT_FF_SESSION_KEY = "mojo-split-factfind-session"
const SPLIT_FF_SESSION_VERSION = 5

const startSplitFactFindSession = (friendlyId: string, postcode: string) => {
  try {
    sessionStorage.setItem(
      SPLIT_FF_SESSION_KEY,
      JSON.stringify({
        friendlyId,
        postcode,
        handoff: null,
        progress: null,
        version: SPLIT_FF_SESSION_VERSION,
        savedAt: Date.now(),
      })
    )
  } catch {
    // Storage can be unavailable. The split fact find will then show its own error page.
  }
}

export const logSplitFfEntrySource = (source: SplitFfEntrySource) =>
  LogGaEvent({
    action: EventAction.splitFfEntrySource,
    event_category: EventCategory.functionalInteraction,
    event_label: source,
  })

/**
 * Sends the user into the split fact find. It is a full page load into another app, so the entry
 * source is logged first, and the session it reads is written just before, along with the
 * `handoff`: the answers it can use that this app already has (see `pickHandoffFields`). Without
 * a handoff it still works, it just has to ask without them.
 */
export const enterSplitFactFind = ({
  friendlyId,
  postcode,
  source,
  handoff = {},
}: {
  friendlyId: string
  postcode: string
  source: SplitFfEntrySource
  handoff?: HandoffFields
}) => {
  logSplitFfEntrySource(source)
  startSplitFactFindSession(friendlyId, postcode)
  // Always written, even when empty, so that an entry left by another application is cleared
  writeHandoff(friendlyId, handoff)
  window.location.assign(SPLIT_FF_PATH)
}
